"use client";

import { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axiosInstance from "../../helpers/Axios";

// --- Styled components are unchanged ---
const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

const Testformpage = () => {
  const [audioZip, setAudioZip] = useState(null);
  const [excelSheet, setExcelSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);
  const { access_token } = useContext(AppStateContext);

  const handleAudioZipChange = (event) => {
    setAudioZip(event.target.files[0]);
  };

  const handleExcelSheetChange = (event) => {
    setExcelSheet(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!excelSheet || !audioZip) {
      alert("Please upload both the Excel file and ZIP file.");
      return;
    }
    if (!access_token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("excel_file", excelSheet);
      formData.append("audio_files", audioZip);

      const response = await axiosInstance.post(
        `${baseURL}/api/trail/word-content/bulk-upload/`,
        formData
      );
      setResponseData(response.data);
    } catch (error) {
      console.error("Error uploading files:", error);
      // This error handling is now more robust, checking for nested errors too
      setResponseData({
        status: "error",
        error:
          error.response?.data?.error ||
          error.response?.data?.data?.error ||
          error.message ||
          "Upload failed! Please check the console and try again.",
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setAudioZip(null);
    setExcelSheet(null);
    setResponseData(null);
    document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
  };
  
  const renderDetailedResults = () => {
    // FIX: Access `responseData.data.results` to get the nested array.
    const results = responseData?.data?.results;

    if (!results || results.length === 0) {
      return null;
    }

    return (
      <div className="mt-6 border border-gray-200 p-3 rounded-lg">
        <Typography variant="h6" className="mb-3 text-gray-800 font-medium">
          Detailed Results ({results.length})
        </Typography>
        <div className="max-h-60 overflow-y-auto pr-1">
          {results.map((result, index) => {
            const colorScheme = {
              failed: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-800", leftBorder: "border-red-400" },
              updated: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-800", leftBorder: "border-blue-400" },
              created: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", badge: "bg-green-100 text-green-800", leftBorder: "border-green-400" },
            };
            const colors = colorScheme[result.status] || colorScheme.failed;
            return (
              <div key={index} className={`${colors.bg} p-4 rounded-lg mb-3 border ${colors.border}`}>
                <p className={`font-medium ${colors.text}`}>Word: {result.word || "Unknown"}</p>
                {result.status && (
                  <p className={`text-xs ${colors.badge} px-2 py-1 mt-1 inline-block rounded`}>
                    Status: {result.status}
                  </p>
                )}
                {result.error && (
                  <div className={`bg-white p-3 rounded border-l-2 ${colors.leftBorder} mt-2`}>
                    <p className={`text-sm ${colors.text} font-medium`}>Error:</p>
                    <p className={`text-sm ${colors.text} mt-1`}>{result.error}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <StyledBox>
      <FormContainer>
        <Typography variant="h4" className="text-center mb-6 font-semibold text-gray-800">
          Upload Bulk Word Audio
        </Typography>
        <form onSubmit={handleSubmit} className="space-y-6">
           <div className="space-y-2">
             <label className="block text-sm font-medium text-gray-700 mb-1">Audio ZIP File</label>
             <TextField type="file" accept=".zip" onChange={handleAudioZipChange} fullWidth variant="outlined" InputProps={{ startAdornment: ( <AudiotrackIcon className="w-5 h-5 text-gray-400 mr-2" /> ), }} />
           </div>
           <div className="space-y-2">
             <label className="block text-sm font-medium text-gray-700 mb-1">Excel Sheet</label>
             <TextField type="file" accept=".xlsx, .xls" onChange={handleExcelSheetChange} fullWidth variant="outlined" InputProps={{ startAdornment: ( <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" /> ), }} />
           </div>
          <StyledButton type="submit" variant="contained" fullWidth className="py-2.5" disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : "Upload Files"}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">Upload Results</Typography>
              <IconButton onClick={handleClose} className="text-gray-400 hover:text-gray-600"><CloseIcon /></IconButton>
            </div>
          </div>
          <DialogContent className="px-6 py-5">
            {responseData && (
              <div className="space-y-4">
                {/* FIX: Check `responseData.status` for success/error distinction */}
                <div className={`flex items-center space-x-3 ${responseData.status === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"} p-4 rounded-lg`}>
                  {responseData.status === "success" ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ErrorIcon className="h-5 w-5 text-red-500" />
                  )}
                  {/* FIX: Access the nested message or the top-level error */}
                  <p className="font-medium">{responseData.data?.message || responseData.error}</p>
                </div>

                {/* FIX: Check for the nested `summary` object */}
                {responseData.data?.summary && (
                  <div className="grid grid-cols-2 gap-4">
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Total Rows</Typography>
                      <Typography variant="h6" className="text-gray-900">
                        {/* FIX: Access nested summary object */}
                        {responseData.data.summary.total_rows}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Failed</Typography>
                      <Typography variant="h6" className="text-red-600">
                        {responseData.data.summary.failed}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Created</Typography>
                      <Typography variant="h6" className="text-green-600">
                        {responseData.data.summary.created}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">Updated</Typography>
                      <Typography variant="h6" className="text-blue-600">
                        {responseData.data.summary.updated}
                      </Typography>
                    </ResultCard>
                  </div>
                )}
                
                {renderDetailedResults()}
              </div>
            )}
          </DialogContent>
          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">Close</StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default Testformpage;
